import { IconBolt, IconClockHour4, IconDotsVertical } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, StatusChip, Text } from "@fragments";
import { UserAvatar } from "@components/Community";
import { isProfileLinkable, profileLinkProps } from "@helpers/profile";
import { usePostSync } from "@helpers/posts";

import PostActions from "../PostActions";

import "./styles.scss";

/**
 * One feed card. The body (text + photo) is the target that opens the post
 * modal; the publisher block links to the profile and the action row sits
 * outside the target so no real button is nested inside it.
 */
export default function Post({
    post: source,
    isAdmin = false,
    onOpen,
    onManage,
    onReact,
    onShare,
}) {
    const { t } = useTranslation("community");
    const post = usePostSync(source);

    if (!post?.publisher) return null;

    const { publisher, text, image, insights, timestamps, scheduled_to: scheduledTo } = post;

    function open() {
        onOpen?.(post);
    }

    function onBodyKeyDown(e) {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
        }
    }

    return (
        <article className="post">
            <div className="post-header">
                <div
                    className={`post-author${isProfileLinkable(publisher) ? " post-author--link" : ""}`}
                    {...profileLinkProps(publisher)}
                >
                    <UserAvatar user={publisher} size={42} link={false} />
                    <div className="post-author__text">
                        <Text.label contrast={10} className="post-author__name">
                            {publisher.username}
                        </Text.label>
                        {/* Managers see a scheduled post ahead of time: its moment replaces the date. */}
                        {scheduledTo ? (
                            <StatusChip.Info
                                className="post-author__scheduled"
                                icon={<IconClockHour4 />}
                                preserveCase
                                text={t("feed.scheduledTo", {
                                    date: window.helpers.pretty.date(scheduledTo),
                                })}
                            />
                        ) : (
                            <Text.caption contrast={6}>
                                {window.helpers.pretty.date(timestamps?.published)}
                            </Text.caption>
                        )}
                    </div>
                </div>

                {insights?.points > 0 && (
                    <Text.caption contrast={6} left={<IconBolt />} className="post__points">
                        {t("feed.points", { value: insights.points })}
                    </Text.caption>
                )}

                {isAdmin && (
                    <Button
                        variant="text"
                        color="inverse"
                        size="small"
                        icon={<IconDotsVertical />}
                        onClick={() => onManage?.(post)}
                        tooltip={t("groupModals:adminPostManage.title")}
                        aria-label={t("groupModals:adminPostManage.title")}
                        preventLoading
                    />
                )}
            </div>

            <div
                className="post__body"
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={onBodyKeyDown}
                aria-label={t("feed.openPost")}
            >
                {text && (
                    <Text.span contrast={9} weight={400} className="post__text">
                        {text}
                    </Text.span>
                )}
                {image && (
                    <div className="media-container">
                        <img src={image} className="photo" alt="" />
                        <img src={image} className="photo-bg" alt="" aria-hidden="true" />
                    </div>
                )}
            </div>

            <div className="post__footer">
                <PostActions
                    post={post}
                    onReact={() => onReact?.(post)}
                    onComment={open}
                    onShare={() => onShare?.(post)}
                />
            </div>
        </article>
    );
}
